<template>
  <form class="space-y-4" @submit.prevent="handleSubmit">
    <div class="rounded-md space-y-3">
      <!-- Premestam ime i prezime na početak forme, jedno ispod drugog -->
      <FormInput
        id="first-name"
        name="firstName"
        type="text"
        label="Ime"
        v-model="formData.firstName"
        placeholder="Ime"
        :error="errors.firstName"
        @blur="validateField('firstName')"
      />

      <FormInput
        id="last-name"
        name="lastName"
        type="text"
        label="Prezime"
        v-model="formData.lastName"
        placeholder="Prezime"
        :error="errors.lastName"
        @blur="validateField('lastName')"
      />

      <FormInput
        id="username"
        name="username"
        type="text"
        label="Korisničko ime"
        v-model="formData.username"
        placeholder="Korisničko ime"
        required
        :error="errors.username"
        @blur="validateField('username')"
        @input="debouncedCheckUsername"
      />

      <FormInput
        id="email-address"
        name="email"
        type="email"
        label="Email adresa"
        v-model="formData.email"
        placeholder="Email adresa"
        required
        :error="errors.email"
        @blur="() => validateField('email')"
        @input="debouncedCheckEmail"
      />

      <!-- Nova PhoneInput komponenta -->
      <PhoneInput
        id="phone-number"
        name="phoneNumber"
        label="Broj telefona"
        v-model="formData.phoneNumber"
        placeholder="Broj telefona (OPCIONO)"
        :error="errors.phoneNumber"
        @blur="validateField('phoneNumber')"
      />

      <FormInput
        id="password"
        name="password"
        type="password"
        label="Lozinka"
        v-model="formData.password"
        placeholder="Lozinka"
        required
        :error="errors.password"
        @blur="validateField('password')"
      />

      <FormInput
        id="confirm-password"
        name="confirm-password"
        type="password"
        label="Potvrdi lozinku"
        v-model="formData.confirmPassword"
        placeholder="Potvrdi lozinku"
        required
        :error="errors.confirmPassword"
        @blur="validateField('confirmPassword')"
      />

      <!-- Zamena standardnog select elementa sa CityDropdown komponentom -->
      <CityDropdown
        id="city"
        v-model="formData.city"
        label="Grad"
        placeholder="Izaberite grad"
        :error="errors.city"
        @blur="validateField('city')"
      />
    </div>

    <FormCheckbox
      id="terms-and-conditions"
      name="termsAndConditions"
      v-model="formData.termsAccepted"
      @change="validateField('termsAccepted')"
    >
      Prihvatam
      <!-- Izmenjeni link koji otvara modal -->
      <button
        type="button"
        class="text-indigo-400 hover:text-indigo-300 focus:outline-none"
        @click.prevent="showTermsModal = true"
      >
        uslove korišćenja
      </button>
    </FormCheckbox>

    <!-- Rate limiting warning -->
    <div
      v-if="rateLimitWarning"
      class="bg-yellow-900 border border-yellow-700 text-yellow-300 px-3 py-2 rounded relative text-sm"
      role="alert"
    >
      <span class="block sm:inline">{{ rateLimitWarning }}</span>
    </div>

    <!-- Server error message -->
    <div
      v-if="serverError"
      class="bg-red-900 border border-red-700 text-red-300 px-3 py-2 rounded relative text-sm"
      role="alert"
    >
      <span class="block sm:inline">{{ serverError }}</span>
    </div>

    <div class="mt-6">
      <FormButton :loading="loading" :disabled="registerBlocked" class="w-full">
        Registruj se
      </FormButton>
    </div>

    <!-- Rate limit cooldown timer -->
    <div v-if="cooldownActive" class="text-center text-sm text-red-400">
      Previše pokušaja registracije. Pokušajte ponovo za
      {{ cooldownTimeRemaining }} sekundi.
    </div>

    <!-- Modal sa uslovima korišćenja -->
    <TermsModal
      :show="showTermsModal"
      @close="showTermsModal = false"
      @accept="acceptTerms"
    />
  </form>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import FormInput from './FormInput.vue';
import FormCheckbox from './FormCheckbox.vue';
import FormButton from './FormButton.vue';
import BaseAuthForm from './BaseAuthForm.vue';
import CityDropdown from '@/components/CityDropdown.vue';
import TermsModal from '@/components/TermsModal.vue';
import PhoneInput from './PhoneInput.vue';
import api from '@/services/api';
import {
  checkUsernameAvailability,
  checkEmailAvailability,
  getUsernameAvailabilityMessage,
  getEmailAvailabilityMessage,
} from '@/services/api';
import securityService from '@/services/security';
import formValidator from '@/services/formValidator';
import { debounce } from '@/utils/debounce';
import emailDomainValidator from '@/services/emailDomainValidator';

interface TouchedFields {
  [key: string]: boolean;
}

interface RegisterFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  city: string;
  phoneNumber: string;
  termsAccepted: boolean;
}

interface RegisterErrors {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  city: string;
  phoneNumber: string;
}

export default defineComponent({
  name: 'RegisterForm',
  extends: BaseAuthForm,
  components: {
    FormInput,
    FormCheckbox,
    FormButton,
    CityDropdown,
    TermsModal,
    PhoneInput,
  },
  data() {
    return {
      formData: {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        city: '',
        phoneNumber: '',
        termsAccepted: false,
      } as RegisterFormData,
      errors: {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        city: '',
        phoneNumber: '',
      } as RegisterErrors,
      touchedFields: {} as TouchedFields,
      usernameChecking: false,
      emailChecking: false,
      debouncedCheckUsername: null as unknown as (value: string) => void,
      debouncedCheckEmail: null as unknown as (value: string) => void,
      showTermsModal: false,
    };
  },
  computed: {
    registerBlocked(): boolean {
      return this.formBlocked || this.usernameChecking || this.emailChecking;
    },
  },
  created() {
    // Kreiramo debounced funkcije prilikom kreiranja komponente
    this.debouncedCheckUsername = debounce(this.checkUsername.bind(this), 500);
    this.debouncedCheckEmail = debounce(this.checkEmail.bind(this), 500);
  },
  methods: {
    /**
     * Nova metoda za prihvatanje uslova korišćenja
     */
    acceptTerms(): void {
      this.formData.termsAccepted = true;
      this.serverError = '';
    },

    /**
     * Backup validacija da li polje sadrži razmake (sigurnosna mera)
     */
    validateNoSpaces(
      value: string,
      fieldName: string
    ): { isValid: boolean; error: string } {
      if (value && /\s/.test(value)) {
        return {
          isValid: false,
          error: `${fieldName} ne sme da sadrži razmake`,
        };
      }
      return { isValid: true, error: '' };
    },

    /**
     * Proverava dostupnost korisničkog imena
     */
    async checkUsername(): Promise<void> {
      const username = this.formData.username;

      // Ne proveravamo prazno korisničko ime ili kraće od 3 karaktera
      if (!username || username.length < 3) {
        return;
      }

      // Osnovnu validaciju radimo prvo
      const validation = formValidator.validateUsername(username);
      if (!validation.isValid) {
        this.errors.username = validation.error;
        return;
      }

      this.usernameChecking = true;

      try {
        const isAvailable = await checkUsernameAvailability(username);

        if (!isAvailable) {
          this.errors.username =
            'Korisničko ime je već zauzeto. Molimo izaberite drugo.';
          this.touchedFields['username'] = true;
        } else {
          // Brisemo grešku samo ako je bila greška o zauzetosti
          if (
            this.errors.username &&
            this.errors.username.includes('zauzeto')
          ) {
            this.errors.username = '';
          }
        }
      } catch (error) {
        console.error('Greška prilikom provere korisničkog imena:', error);
      } finally {
        this.usernameChecking = false;
      }
    },

    /**
     * Proverava dostupnost email adrese (UPDATED)
     */
    async checkEmail(): Promise<void> {
      const email = this.formData.email;

      // Ne proveravamo prazan email ili neispravne formate
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return;
      }

      // NOVA ASINHRONA VALIDACIJA sa GitHub blacklist
      const validation = await formValidator.validateEmailAsync(email);
      if (!validation.isValid) {
        this.errors.email = validation.error;
        return;
      }

      this.emailChecking = true;

      try {
        const isAvailable = await checkEmailAvailability(email);

        if (!isAvailable) {
          this.errors.email =
            'Ova email adresa je već registrovana. Probajte sa drugom.';
          this.touchedFields['email'] = true;
        } else {
          // Brisemo grešku samo ako je bila greška o zauzetosti
          if (this.errors.email && this.errors.email.includes('registrovana')) {
            this.errors.email = '';
          }
        }
      } catch (error) {
        console.error('Greška prilikom provere email adrese:', error);
      } finally {
        this.emailChecking = false;
      }
    },

    /**
     * Validira telefonski broj u srpskom formatu koji se konvertuje u međunarodni
     */
    validatePhoneNumber(phoneNumber: string): {
      isValid: boolean;
      error: string;
    } {
      // Ako je prazno, prihvatamo jer je polje opciono
      if (!phoneNumber) {
        return { isValid: true, error: '' };
      }

      // Proveravamo različite formate srpskih brojeva u međunarodnom formatu
      const phoneRegex = /^\+381(6[0-9]|63|64|65)\d{6,7}$/;
      if (!phoneRegex.test(phoneNumber)) {
        return {
          isValid: false,
          error: 'Broj telefona mora biti u formatu 06XXXXXXX (8-10 cifara)',
        };
      }

      return { isValid: true, error: '' };
    },

    /**
     * Validira specifično polje kada izgubi fokus (UPDATED)
     */
    async validateField(field: string): Promise<void> {
      // Označavamo polje kao dodirnuto
      this.touchedFields[field] = true;

      // Validiramo samo polja koja su dodirnuta
      switch (field) {
        case 'username':
          // Backup validacija za razmake
          const usernameSpaceValidation = this.validateNoSpaces(
            this.formData.username,
            'Korisničko ime'
          );
          if (!usernameSpaceValidation.isValid) {
            this.errors.username = usernameSpaceValidation.error;
            return;
          }

          const usernameValidation = formValidator.validateUsername(
            this.formData.username
          );
          // Postavi grešku samo ako ne postoji već greška o zauzetosti
          if (
            !this.errors.username ||
            !this.errors.username.includes('zauzeto')
          ) {
            this.errors.username = usernameValidation.error;
          }
          // Prilikom blur eventa, pokrećemo i proveru dostupnosti
          if (this.formData.username && this.formData.username.length >= 3) {
            this.checkUsername();
          }
          break;

        case 'email':
          // Backup validacija za razmake
          const emailSpaceValidation = this.validateNoSpaces(
            this.formData.email,
            'Email adresa'
          );
          if (!emailSpaceValidation.isValid) {
            this.errors.email = emailSpaceValidation.error;
            return;
          }

          // NOVA ASINHRONA VALIDACIJA
          const emailValidation = await formValidator.validateEmailAsync(
            this.formData.email
          );

          // Postavi grešku samo ako ne postoji već greška o zauzetosti
          if (
            !this.errors.email ||
            !this.errors.email.includes('registrovana')
          ) {
            this.errors.email = emailValidation.error;
          }
          // Prilikom blur eventa, pokrećemo i proveru dostupnosti
          if (
            emailValidation.isValid &&
            this.formData.email &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)
          ) {
            this.checkEmail();
          }
          break;

        case 'phoneNumber':
          // Validiramo format telefona
          const phoneValidation = this.validatePhoneNumber(
            this.formData.phoneNumber
          );
          this.errors.phoneNumber = phoneValidation.error;
          break;

        case 'password':
          // Backup validacija za razmake
          const passwordSpaceValidation = this.validateNoSpaces(
            this.formData.password,
            'Lozinka'
          );
          if (!passwordSpaceValidation.isValid) {
            this.errors.password = passwordSpaceValidation.error;
            return;
          }

          const passwordValidation = formValidator.validatePassword(
            this.formData.password
          );
          this.errors.password = passwordValidation.error;

          // Ako je confirm password već dodirnut, validiramo i njega ponovo
          if (this.touchedFields['confirmPassword']) {
            const matchValidation = formValidator.validatePasswordMatch(
              this.formData.password,
              this.formData.confirmPassword
            );
            this.errors.confirmPassword = matchValidation.error;
          }
          break;

        case 'confirmPassword':
          // Backup validacija za razmake
          const confirmPasswordSpaceValidation = this.validateNoSpaces(
            this.formData.confirmPassword,
            'Potvrda lozinke'
          );
          if (!confirmPasswordSpaceValidation.isValid) {
            this.errors.confirmPassword = confirmPasswordSpaceValidation.error;
            return;
          }

          const matchValidation = formValidator.validatePasswordMatch(
            this.formData.password,
            this.formData.confirmPassword
          );
          this.errors.confirmPassword = matchValidation.error;
          break;

        case 'firstName':
          // Backup validacija za razmake
          const firstNameSpaceValidation = this.validateNoSpaces(
            this.formData.firstName,
            'Ime'
          );
          if (!firstNameSpaceValidation.isValid) {
            this.errors.firstName = firstNameSpaceValidation.error;
            return;
          }

          const firstNameValidation = formValidator.validateRequired(
            this.formData.firstName,
            'Ime'
          );
          this.errors.firstName = firstNameValidation.error;
          break;

        case 'lastName':
          // Backup validacija za razmake
          const lastNameSpaceValidation = this.validateNoSpaces(
            this.formData.lastName,
            'Prezime'
          );
          if (!lastNameSpaceValidation.isValid) {
            this.errors.lastName = lastNameSpaceValidation.error;
            return;
          }

          const lastNameValidation = formValidator.validateRequired(
            this.formData.lastName,
            'Prezime'
          );
          this.errors.lastName = lastNameValidation.error;
          break;

        case 'city':
          this.errors.city = this.formData.city ? '' : 'Grad je obavezan';
          break;

        case 'termsAccepted':
          this.serverError = this.formData.termsAccepted
            ? ''
            : 'Morate prihvatiti uslove korišćenja';
          break;
      }
    },

    /**
     * Glavna validacija forme (UPDATED)
     */
    async validateForm(): Promise<boolean> {
      this.errors = {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        city: '',
        phoneNumber: '',
      };

      let isValid = true;

      // Validacija korisničkog imena
      const usernameSpaceValidation = this.validateNoSpaces(
        this.formData.username,
        'Korisničko ime'
      );
      if (!usernameSpaceValidation.isValid) {
        this.errors.username = usernameSpaceValidation.error;
        isValid = false;
      } else {
        const usernameValidation = formValidator.validateUsername(
          this.formData.username
        );
        if (!usernameValidation.isValid) {
          this.errors.username = usernameValidation.error;
          isValid = false;
        }
      }

      // Validacija email adrese
      const emailSpaceValidation = this.validateNoSpaces(
        this.formData.email,
        'Email adresa'
      );
      if (!emailSpaceValidation.isValid) {
        this.errors.email = emailSpaceValidation.error;
        isValid = false;
      } else {
        // NOVA ASINHRONA EMAIL VALIDACIJA
        const emailValidation = await formValidator.validateEmailAsync(
          this.formData.email
        );
        if (!emailValidation.isValid) {
          this.errors.email = emailValidation.error;
          isValid = false;
        }
      }

      // Validacija lozinke
      const passwordSpaceValidation = this.validateNoSpaces(
        this.formData.password,
        'Lozinka'
      );
      if (!passwordSpaceValidation.isValid) {
        this.errors.password = passwordSpaceValidation.error;
        isValid = false;
      } else {
        const passwordValidation = formValidator.validatePassword(
          this.formData.password
        );
        if (!passwordValidation.isValid) {
          this.errors.password = passwordValidation.error;
          isValid = false;
        }
      }

      // Validacija potvrde lozinke
      const confirmPasswordSpaceValidation = this.validateNoSpaces(
        this.formData.confirmPassword,
        'Potvrda lozinke'
      );
      if (!confirmPasswordSpaceValidation.isValid) {
        this.errors.confirmPassword = confirmPasswordSpaceValidation.error;
        isValid = false;
      } else {
        const passwordMatchValidation = formValidator.validatePasswordMatch(
          this.formData.password,
          this.formData.confirmPassword
        );
        if (!passwordMatchValidation.isValid) {
          this.errors.confirmPassword = passwordMatchValidation.error;
          isValid = false;
        }
      }

      // Validacija imena
      const firstNameSpaceValidation = this.validateNoSpaces(
        this.formData.firstName,
        'Ime'
      );
      if (!firstNameSpaceValidation.isValid) {
        this.errors.firstName = firstNameSpaceValidation.error;
        isValid = false;
      } else {
        const firstNameValidation = formValidator.validateRequired(
          this.formData.firstName,
          'Ime'
        );
        if (!firstNameValidation.isValid) {
          this.errors.firstName = firstNameValidation.error;
          isValid = false;
        }
      }

      // Validacija prezimena
      const lastNameSpaceValidation = this.validateNoSpaces(
        this.formData.lastName,
        'Prezime'
      );
      if (!lastNameSpaceValidation.isValid) {
        this.errors.lastName = lastNameSpaceValidation.error;
        isValid = false;
      } else {
        const lastNameValidation = formValidator.validateRequired(
          this.formData.lastName,
          'Prezime'
        );
        if (!lastNameValidation.isValid) {
          this.errors.lastName = lastNameValidation.error;
          isValid = false;
        }
      }

      // Validacija broja telefona (ako je unet)
      const phoneValidation = this.validatePhoneNumber(
        this.formData.phoneNumber
      );
      if (!phoneValidation.isValid) {
        this.errors.phoneNumber = phoneValidation.error;
        isValid = false;
      }

      // Validacija grada
      if (!this.formData.city) {
        this.errors.city = 'Grad je obavezan';
        isValid = false;
      }

      // Validacija uslova korišćenja
      if (!this.formData.termsAccepted) {
        this.serverError = 'Morate prihvatiti uslove korišćenja';
        isValid = false;
      }

      return isValid;
    },

    /**
     * Submit handler (UPDATED)
     */
    async handleSubmit(): Promise<void> {
      // Označavamo sva polja kao dodirnuta
      const allFields = [
        'username',
        'email',
        'password',
        'confirmPassword',
        'firstName',
        'lastName',
        'city',
        'phoneNumber',
        'termsAccepted',
      ];

      allFields.forEach((field) => {
        this.touchedFields[field] = true;
      });

      // Proveravamo dostupnost korisničkog imena i email-a pre slanja zahteva
      await Promise.all([this.checkUsername(), this.checkEmail()]);

      // Ako ima grešaka sa dostupnošću, ne šaljemo zahtev
      if (
        (this.errors.username && this.errors.username.includes('zauzeto')) ||
        (this.errors.email && this.errors.email.includes('registrovana'))
      ) {
        return;
      }

      // ASINHRONA VALIDACIJA FORME
      const isFormValid = await this.validateForm();
      if (!isFormValid) {
        return;
      }

      // Provera rate limiting-a
      if (!securityService.trackLoginAttempt()) {
        this.startCooldownTimer();
        this.serverError = 'Previše pokušaja registracije. Molimo sačekajte.';
        return;
      }

      // Ažuriramo upozorenje o rate limiting-u
      this.updateRateLimitWarning();

      this.loading = true;
      this.serverError = '';

      try {
        const csrfToken = await securityService.getCsrfToken();

        if (!csrfToken) {
          throw new Error(
            'Nije moguće dobiti sigurnosni token. Molimo pokušajte ponovo kasnije.'
          );
        }

        const requestPayload: any = {
          firstName: this.formData.firstName || '',
          lastName: this.formData.lastName || '',
          username: this.formData.username,
          email: this.formData.email,
          password: this.formData.password,
          city: this.formData.city,
        };

        if (
          this.formData.phoneNumber &&
          this.formData.phoneNumber.trim() !== ''
        ) {
          requestPayload.phoneNumber = this.formData.phoneNumber.trim();
        }

        const response = await api.post('/api/auth/register', requestPayload, {
          headers: {
            'X-CSRF-TOKEN': csrfToken,
          },
          withCredentials: true,
        });

        this.processAuthResponse(response.data, 'register-success');
      } catch (error) {
        this.handleApiError(error, 'register');
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    // Osiguravamo da je CSRF token dostupan
    securityService.refreshCsrfToken();
  },
});
</script>
